import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Phone, MessageCircle, MapPin, Inbox, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";

/** Admin > Contatos: what the website shows and who receives each kind of message (system_settings "contact.*"). */
interface ContactSettings {
  email: string;
  phone: string;
  whatsApp: string;
  address: string;
  privacyEmail: string;
  legalEmail: string;
  supportEmail: string;
  supportRecipients: string[];
  salesRecipients: string[];
  defaults: { email: string; address: string; supportRecipients: string[]; salesRecipients: string[] };
}

interface ContactRequest {
  id: string;
  createdAt: string;
  name: string;
  email: string;
  company: string;
  phone?: string | null;
  companySize?: string | null;
  interest?: string | null;
  message?: string | null;
  source?: string | null;
}

const SETTINGS_KEY = ["/api/admin/contact-settings"];
const REQUESTS_KEY = ["/api/admin/contact-requests"];

/** Where each kind of message goes (shown next to the two lists so the admin sees the whole map). */
const CHANNELS: { list: "support" | "sales"; text: string }[] = [
  { list: "support", text: "Formulário de suporte do app (Ajuda)" },
  { list: "support", text: "Chat do app, quando a pessoa pede atendimento humano" },
  { list: "support", text: "WhatsApp da FillSign, quando a pessoa pede atendimento" },
  { list: "sales", text: "Site: formulário Fale com nossa equipe" },
  { list: "sales", text: "Site: chat, quando o visitante deixa o contato" },
  { list: "sales", text: "App: plano Custom, Falar com vendas" },
];

const INTEREST: Record<string, string> = {
  dynamics365: "Dynamics 365",
  api: "API",
  govbr: "GOV.BR",
  enterprise: "Enterprise",
  other: "Outro",
};

async function readError(res: Response): Promise<string> {
  try {
    const j = await res.json();
    return j?.message || "Erro ao salvar";
  } catch {
    return "Erro ao salvar";
  }
}

export function ContactSettingsCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<ContactSettings>({
    queryKey: SETTINGS_KEY,
    queryFn: async () => (await apiRequest("GET", "/api/admin/contact-settings")).json(),
  });
  const [form, setForm] = useState({ email: "", phone: "", whatsApp: "", address: "", privacyEmail: "", legalEmail: "", supportEmail: "", supportRecipients: "", salesRecipients: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm({
      email: data.email,
      phone: data.phone,
      whatsApp: data.whatsApp,
      address: data.address,
      privacyEmail: data.privacyEmail,
      legalEmail: data.legalEmail,
      supportEmail: data.supportEmail,
      supportRecipients: data.supportRecipients.join("\n"),
      salesRecipients: data.salesRecipients.join("\n"),
    });
  }, [data]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl("/api/admin/contact-settings"), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const msg = await readError(res);
        setError(msg);
        toast({ title: msg, variant: "destructive" });
        return;
      }
      queryClient.setQueryData(SETTINGS_KEY, await res.json());
      toast({ title: "Contatos salvos", description: "O site e os avisos passam a usar em até 1 minuto." });
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const placeholderEmail = form.email || data?.defaults.email || "contato@fillsign.com.br";

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">Contatos e destinos das mensagens</CardTitle>
        <CardDescription>O que aparece no site e para quem vai cada mensagem que as pessoas mandam.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando</div>
        ) : (
          <>
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">No site (fillsign.com.br)</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="c-email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />E-mail de contato</Label>
                  <Input id="c-email" type="email" value={form.email} onChange={set("email")} placeholder="contato@fillsign.com.br" data-testid="input-contact-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-address" className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Cidade exibida</Label>
                  <Input id="c-address" value={form.address} onChange={set("address")} placeholder="Belo Horizonte, MG - Brasil" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone" className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Telefone</Label>
                  <Input id="c-phone" value={form.phone} onChange={set("phone")} placeholder="Em branco: não aparece" data-testid="input-contact-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-wa" className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</Label>
                  <Input id="c-wa" value={form.whatsApp} onChange={set("whatsApp")} placeholder="Em branco: não aparece. Ex.: +55 31 98888-7777" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-privacy">E-mail de privacidade (LGPD)</Label>
                  <Input id="c-privacy" type="email" value={form.privacyEmail} onChange={set("privacyEmail")} placeholder={`Em branco: ${placeholderEmail}`} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-legal">E-mail jurídico (Termos e Licença)</Label>
                  <Input id="c-legal" type="email" value={form.legalEmail} onChange={set("legalEmail")} placeholder={`Em branco: ${placeholderEmail}`} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="c-support">E-mail de suporte exibido no app (Ajuda) e pelos assistentes</Label>
                  <Input id="c-support" type="email" value={form.supportEmail} onChange={set("supportEmail")} placeholder={`Em branco: ${placeholderEmail}`} />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Quem recebe</h3>
              <p className="text-xs text-muted-foreground">Um e-mail por linha (ou separados por vírgula), até 10 por lista. Esses endereços não aparecem no site.</p>
              <div className="grid gap-4 md:grid-cols-2">
                {(["support", "sales"] as const).map((list) => (
                  <div key={list} className="space-y-2 rounded-lg border p-3">
                    <Label htmlFor={`c-${list}`} className="font-semibold">{list === "support" ? "Suporte" : "Comercial"}</Label>
                    <Textarea
                      id={`c-${list}`}
                      rows={4}
                      value={list === "support" ? form.supportRecipients : form.salesRecipients}
                      onChange={set(list === "support" ? "supportRecipients" : "salesRecipients")}
                      className="font-mono text-xs"
                      data-testid={`textarea-${list}-recipients`}
                    />
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      {CHANNELS.filter((c) => c.list === list).map((c) => (
                        <li key={c.text} className="flex gap-1.5"><span aria-hidden>•</span><span>{c.text}</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="button-save-contacts">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salvar contatos
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function ContactRequestsCard() {
  const { data, isLoading } = useQuery<ContactRequest[]>({
    queryKey: REQUESTS_KEY,
    queryFn: async () => (await apiRequest("GET", "/api/admin/contact-requests")).json(),
  });
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Inbox className="h-4 w-4" />Mensagens do site ({data?.length ?? 0})</CardTitle>
        <CardDescription>Quem preencheu Fale com nossa equipe. Também chegam por e-mail para a lista Comercial.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando</div>
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma mensagem ainda.</p>
        ) : (
          <ul className="divide-y">
            {data.map((r) => {
              const expanded = open === r.id;
              const msg = r.message || "";
              return (
                <li key={r.id} className="py-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <div className="min-w-0">
                      <span className="font-medium">{r.name}</span>
                      <span className="text-muted-foreground"> · {r.company}</span>
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums">{new Date(r.createdAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                    <a href={`mailto:${r.email}`} className="text-primary hover:underline break-all">{r.email}</a>
                    {r.phone && <span className="text-muted-foreground">{r.phone}</span>}
                    {r.interest && <Badge variant="secondary">{INTEREST[r.interest] ?? r.interest}</Badge>}
                    {r.companySize && <Badge variant="outline">{r.companySize} pessoas</Badge>}
                  </div>
                  {msg && (
                    <p className={`mt-1.5 whitespace-pre-wrap text-sm text-muted-foreground ${expanded ? "" : "line-clamp-2"}`}>
                      {msg}
                    </p>
                  )}
                  {msg.length > 160 && (
                    <button type="button" className="mt-1 text-xs text-primary hover:underline" onClick={() => setOpen(expanded ? null : r.id)}>
                      {expanded ? "Mostrar menos" : "Ler tudo"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
